import { useEffect, useMemo, useState } from 'preact/hooks';
import { AppShell } from '../components/AppShell';
import { InMemoryPointBindingSource } from '../hmi/InMemorySource';
import { PointBindingSourceProvider } from '../hmi/PointBindingProvider';
import { WidgetHost } from '../hmi/WidgetHost';
import type { PointQuality } from '../hmi/binding';
import { formatAgeDe } from '../hmi/quality';
import type { WidgetDefinition } from '../hmi/widgets';
import './HmiPreviewPage.css';

/**
 * Widget-Katalog + Simulator-Playground fuer HMI-Widgets. Bootstrapped eine
 * frische <see cref="InMemoryPointBindingSource"/>, seedet ~15 fake DPs (Temp,
 * Setpoint, Mode-Enum, Kompressor, Energie-Counter etc.), tickt sie mit einem
 * kleinen Sim-Loop, und rendert jeden verfuegbaren Widget-Kind gegen die Sim.
 *
 * Die Binding-Keys hier sind bewusst `demo/...`-Strings und keine
 * `{plantId}/{leafId}`-Guids. Das ist der Sonderfall des Simulators: der
 * Live-Adapter (`hmi/SignalRSource.ts`) loest `demo/`-Keys NIE auf, damit
 * eine Katalog-Definition, die versehentlich in ein echtes Projekt wandert,
 * sichtbar ins Leere zeigt statt gegen eine echte Anlage zu schreiben.
 * Regeln dazu: `hmi/bindingKey.ts`.
 *
 * Zweck: 1) End-to-end Beweis dass Definitionsmodell + Binding + Preact-
 * Renderer zusammenspielen, 2) Referenz-Layout das die HVACStudio-Widget-
 * Katalog-UI spaeter wiederverwenden kann, 3) Tab-gruppierte Uebersicht
 * "welche Widgets gibt es und wie sehen sie aus" fuer neue Team-Mitglieder.
 */
type TabKey = 'command' | 'display' | 'hvac' | 'compound' | 'assets';
const TABS: { key: TabKey; label: string; hint: string }[] = [
  { key: 'command',  label: 'Kommando',       hint: 'Aktionen, die auf einen Regler schreiben' },
  { key: 'display',  label: 'Anzeige',        hint: 'Wertanzeige, Zustände, Trends' },
  { key: 'hvac',     label: 'HVAC-spezial',   hint: 'Anlagenschema, Sankey, Wochenprogramm, Kaskaden' },
  { key: 'compound', label: 'Compound-Tiles', hint: 'Ganze Objekte pro Kachel (Heizkreis, WP, WW, Raum, PV, Wetter) — Building-Blocks für AI-Composition' },
  { key: 'assets',   label: 'Kunden-Assets',  hint: 'Kundeneigene SVG-Schemata / Bilder als Widget-Hintergrund' },
];

/**
 * Zustands-Vorführung.
 *
 * Warum das hier sein muss: die Live-Quelle leitet `stale` aus dem ALTER
 * eines Werts ab, und der Simulator wiederholt nie einen unveränderten
 * Wert. Ohne diesen Wähler kann niemand vor dem ersten Feldeinsatz
 * beurteilen, wie eine Kachel aussieht, wenn ihr Wert 40 Minuten alt ist,
 * aus einem Force kommt oder der Transport weg ist — und genau das ist der
 * Zustand, in dem eine Fehlanzeige teuer wird.
 *
 * `ageMs` datiert die Empfangszeit zurück. Bei `stale` sind es 40 Minuten,
 * weil das der Fall aus der Anforderung ist ("Wert ist 21,5 °C, aber 40
 * Minuten alt").
 */
interface SimState {
  key: string;
  label: string;
  hint: string;
  quality: PointQuality;
  ageMs: number;
}

const SIM_STATES: SimState[] = [
  { key: 'live', label: 'Live', quality: 'good', ageMs: 0,
    hint: 'Normalfall: frische Messung, Qualität Good.' },
  { key: 'stale', label: 'Veraltet (40 min)', quality: 'stale', ageMs: 40 * 60_000,
    hint: 'Wert steht noch da, ist aber 40 Minuten alt. Der Fall, der ohne Anzeige gefährlich ist.' },
  { key: 'uncertain', label: 'Unsicher', quality: 'uncertain', ageMs: 20_000,
    hint: 'Wert da, Vertrauen begrenzt. Gilt als degradiert — angeglichen an realtime/freshness.ts.' },
  { key: 'override', label: 'Override (Force)', quality: 'override', ageMs: 0,
    hint: 'Wert kommt aus einem Force, nicht vom Sensor. Aktuell, aber nicht die Anlage.' },
  { key: 'bad', label: 'Ungültig', quality: 'bad', ageMs: 0,
    hint: 'Letzter Leseversuch hat geworfen.' },
  { key: 'disconnected', label: 'Getrennt', quality: 'disconnected', ageMs: 90_000,
    hint: 'Transport zum Gerät weg. Der letzte bekannte Wert ist nicht der aktuelle.' },
];

export function HmiPreviewPage() {
  const [tab, setTab] = useState<TabKey>('command');
  const [simStateKey, setSimStateKey] = useState<string>('live');
  const source = useMemo(() => new InMemoryPointBindingSource({ echoLatencyMs: 90 }), []);
  const simState = SIM_STATES.find((s) => s.key === simStateKey) ?? SIM_STATES[0];

  useEffect(() => {
    // Jeder Sim-Publish geht durch diesen Helfer, damit der Zustands-Wähler
    // eine Stelle hat und nicht dreissig. Qualität und Rückdatierung kommen
    // aus dem gewählten Zustand.
    const pub = (pointId: string, value: unknown) =>
      source.publish(pointId, value, simState.quality, {
        receivedAt: Date.now() - simState.ageMs,
      });

    // Seeds
    source.seed('demo/room-temperature',    21.4);
    source.seed('demo/room-setpoint',       21.5);
    source.seed('demo/supply-temperature',  42.1);
    source.seed('demo/humidity',            47);
    source.seed('demo/pump-load',           0.62);
    source.seed('demo/mode',                'heating');
    source.seed('demo/compressor',          'off');
    source.seed('demo/valve-open',          false);
    source.seed('demo/fan-stage',           2);
    source.seed('demo/energy-kwh',          128740);
    source.seed('demo/runtime-hours',       15320);
    source.seed('demo/cop',                 3.85);
    source.seed('demo/alarm-count',         0);
    // Compound-Tile seeds — one full "Wohnhaus"-Anlage im Simulator.
    source.seed('demo/hp/electrical-power', 2450);
    source.seed('demo/hp/thermal-power',    9800);
    source.seed('demo/hp/state',            'Running');
    source.seed('demo/hp/flow',             43.2);
    source.seed('demo/hp/return',           37.6);
    source.seed('demo/hp/starts-today',     4);
    source.seed('demo/hp/runtime-today',    9720);   // seconds → 2h 42m
    source.seed('demo/hp/runtime-lifetime', 55152000);
    source.seed('demo/hc/valve',            62);
    source.seed('demo/hc/pump',             true);
    source.seed('demo/hw/top',              54.3);
    source.seed('demo/hw/mid',              47.8);
    source.seed('demo/hw/bottom',           35.1);
    source.seed('demo/hw/setpoint',         52);
    source.seed('demo/hw/state',            'Idle');
    source.seed('demo/hw/next-legionella',  new Date(Date.now() + 4 * 24 * 3600 * 1000).toISOString());
    source.seed('demo/room/humidity',       46);
    source.seed('demo/room/presence',       true);
    source.seed('demo/room/window',         false);
    source.seed('demo/outside/temp',        -3.2);
    source.seed('demo/outside/attenuated',  -0.8);
    source.seed('demo/outside/wind',        14.5);
    source.seed('demo/outside/icon',        'schnee');
    source.seed('demo/pv/production',       1820);
    source.seed('demo/pv/household',        2600);
    source.seed('demo/pv/grid',             -780);   // negative = drawing
    source.seed('demo/pv/battery-soc',      68);
    source.seed('demo/pv/battery-power',    -320);   // negative = discharging
    source.seed('demo/pv/daily-production', 24300);  // Wh
    source.seed('demo/pv/daily-self',       18100);
    source.seed('demo/pv/daily-feed',       6200);
    source.seed('demo/pv/daily-grid',       4500);

    // Sofort wirken: ein Wähler, der erst beim nächsten Sim-Tick greift,
    // wirkt kaputt. `seed` oben stempelt immer `good`/jetzt, deshalb hier
    // einmal alles auf den gewählten Zustand ziehen.
    source.republishAll(simState.quality, { receivedAt: Date.now() - simState.ageMs });

    const phase = { t: 0 };
    const modeCycle = ['heating', 'idle', 'defrost', 'cooling'];
    const hpStateCycle = ['Standby', 'Starting', 'Running', 'Running', 'Defrost', 'Running'];
    const iconCycle = ['sonnig', 'bewoelkt', 'regen', 'schnee'];
    const timer = window.setInterval(() => {
      // Bei einem gewollt veralteten Zustand steht der Sim-Loop still.
      // Sonst zeigte der Katalog eine "40 Minuten alte" Zahl, die sich
      // jede Sekunde ändert — und genau das ist nicht, was Veraltetsein
      // bedeutet: es kommt eben nichts mehr. Für `bad` und `override`
      // (Alter 0) laufen die Werte weiter, denn die sind aktuell und nur
      // mit einem Vorbehalt versehen.
      if (simState.ageMs > 0) return;
      phase.t += 1;
      pub('demo/room-temperature',   21.4 + Math.sin(phase.t / 20) * 0.6);
      pub('demo/supply-temperature', 42.1 + Math.sin(phase.t / 8) * 6);
      pub('demo/humidity',           47 + Math.sin(phase.t / 40) * 8);
      pub('demo/pump-load',          0.55 + Math.sin(phase.t / 6) * 0.25);
      pub('demo/cop',                3.85 + Math.sin(phase.t / 12) * 0.55);
      pub('demo/energy-kwh',         128740 + phase.t * 0.6);
      pub('demo/runtime-hours',      15320 + phase.t / 60);
      if (phase.t % 15 === 0) pub('demo/mode', modeCycle[(phase.t / 15) % modeCycle.length]);
      if (phase.t % 7 === 0)  pub('demo/compressor', phase.t % 14 === 0 ? 'on' : 'off');
      // Compound-Sim
      pub('demo/hp/electrical-power', 2450 + Math.sin(phase.t / 10) * 380);
      pub('demo/hp/thermal-power',    9800 + Math.sin(phase.t / 10) * 1500);
      pub('demo/hp/flow',             43.2 + Math.sin(phase.t / 15) * 3);
      pub('demo/hp/return',           37.6 + Math.sin(phase.t / 15) * 2);
      pub('demo/hp/runtime-today',    9720 + phase.t);
      if (phase.t % 30 === 0) pub('demo/hp/state', hpStateCycle[(phase.t / 30) % hpStateCycle.length]);
      pub('demo/hc/valve',            62 + Math.sin(phase.t / 25) * 15);
      pub('demo/hw/top',              54.3 + Math.sin(phase.t / 30) * 1.2);
      pub('demo/room/humidity',       46 + Math.sin(phase.t / 50) * 6);
      pub('demo/outside/temp',        -3.2 + Math.sin(phase.t / 100) * 5);
      pub('demo/outside/wind',        14.5 + Math.sin(phase.t / 8) * 6);
      if (phase.t % 45 === 0) pub('demo/outside/icon', iconCycle[(phase.t / 45) % iconCycle.length]);
      pub('demo/pv/production',       1820 + Math.sin(phase.t / 25) * 900);
      pub('demo/pv/household',        2600 + Math.sin(phase.t / 12) * 500);
      pub('demo/pv/grid',             -780 + Math.sin(phase.t / 20) * 900);
      pub('demo/pv/battery-soc',      Math.max(0, Math.min(100, 68 + Math.sin(phase.t / 80) * 15)));
      pub('demo/pv/battery-power',    Math.sin(phase.t / 15) * 800);
      pub('demo/pv/daily-production', 24300 + phase.t * 2);
    }, 1000);
    return () => window.clearInterval(timer);
  }, [source, simState]);

  const cards = WIDGETS_BY_TAB[tab];

  return (
    <AppShell title="HMI-Widget-Katalog">
      <div class="hv-hmi-preview">
        <div class="hv-hmi-preview__intro">
          <h2>Widget-Playground</h2>
          <p>
            Simulator-Datenquelle in-Memory. Zeigt alle Widget-Kinds aus dem
            Definitionsmodell (Datei <code>src/core/HVACloud.Core.Hmi.Schema</code>).
            Renderer noch nicht implementierte Kinds fallen auf einen
            Platzhalter zurück — Fundament steht, das Rendering-Delta fällt
            pro Widget zunehmend weg.
          </p>
        </div>

        <div class="hv-hmi-preview__tabs" role="tablist">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={tab === t.key}
              class={`hv-hmi-preview__tab${tab === t.key ? ' hv-hmi-preview__tab--active' : ''}`}
              onClick={() => setTab(t.key)}
              title={t.hint}
            >
              {t.label}
              <span class="hv-hmi-preview__tab-count">{(WIDGETS_BY_TAB[t.key] ?? []).length}</span>
            </button>
          ))}
        </div>

        <div class="hv-hmi-preview__tab-hint muted">
          {TABS.find((t) => t.key === tab)?.hint}
        </div>

        {/* Zustands-Vorführung. Ohne die gibt es keine Möglichkeit, das
            Verhalten bei veralteten, geforcten oder ungültigen Werten VOR
            dem ersten Feldeinsatz zu beurteilen — der Simulator liefert von
            sich aus nur frische Good-Werte. */}
        <div class="hv-hmi-preview__sim" role="group" aria-label="Datenqualität simulieren">
          <span class="hv-hmi-preview__sim-label">Zustand simulieren</span>
          <div class="hv-hmi-preview__sim-buttons">
            {SIM_STATES.map((state) => (
              <button
                key={state.key}
                type="button"
                aria-pressed={state.key === simStateKey}
                class={`hv-hmi-preview__sim-btn${state.key === simStateKey ? ' hv-hmi-preview__sim-btn--active' : ''}`}
                onClick={() => setSimStateKey(state.key)}
                title={state.hint}
              >
                {state.label}
              </button>
            ))}
          </div>
          <span class="hv-hmi-preview__sim-hint muted">
            {simState.hint}
            {simState.ageMs > 0 && (
              <> Empfangszeit zurückdatiert: {formatAgeDe({ receivedAt: Date.now() - simState.ageMs })}.</>
            )}
          </span>
        </div>

        {/* Der Simulator ist der SONDERFALL: er stellt seine Quelle explizit
            in den Context, waehrend die produktive Route
            (`/plants/:id/hmi`) den LivePlantBindingProvider nimmt. Die
            Widgets selbst sehen keinen Unterschied — sie holen die Quelle
            beide Mal ueber WidgetHost aus dem Context. */}
        <PointBindingSourceProvider source={source}>
        <div class="hv-hmi-preview__grid">
          {cards.map((w) => (
            <div key={w.id} class="hv-hmi-preview__cell">
              <div class="hv-hmi-preview__cell-kind">{w.kind}</div>
              <WidgetHost widget={w} />
            </div>
          ))}
          {cards.length === 0 && (
            <div class="hv-hmi-preview__empty muted">
              Für diesen Tab liegen noch keine Beispiel-Widgets vor.
              Kommt bald.
            </div>
          )}
        </div>
        </PointBindingSourceProvider>
      </div>
    </AppShell>
  );
}

// ---------------------------------------------------------------- catalog

const COMMAND_WIDGETS: WidgetDefinition[] = [
  {
    kind: 'toggle',
    id: 'w-toggle-compressor',
    label: 'Kompressor',
    commandPoint: 'demo/compressor',
    feedbackPoint: 'demo/compressor',
    onValue: 'on',
    offValue: 'off',
    onLabel: 'Ein',
    offLabel: 'Aus',
  },
  {
    kind: 'enum-setter',
    id: 'w-enum-mode',
    label: 'Betriebsmodus',
    commandPoint: 'demo/mode',
    feedbackPoint: 'demo/mode',
    renderHint: 'auto',
    options: [
      { value: 'heating', label: 'Heizen',  colour: '#d0a24c' },
      { value: 'cooling', label: 'Kühlen',  colour: '#5aa2ff' },
      { value: 'idle',    label: 'Bereit',  colour: '#8a929e' },
      { value: 'defrost', label: 'Abtauen', colour: '#d15c5c' },
    ],
  },
  {
    kind: 'enum-setter',
    id: 'w-enum-fan-stages',
    label: 'Lüfterstufe (Tiles, 6 Werte)',
    commandPoint: 'demo/fan-stage',
    feedbackPoint: 'demo/fan-stage',
    renderHint: 'tile',
    options: [
      { value: 0, label: 'Aus' },
      { value: 1, label: '1' },
      { value: 2, label: '2' },
      { value: 3, label: '3' },
      { value: 4, label: '4' },
      { value: 5, label: 'Boost' },
    ],
  },
  {
    kind: 'setpoint-stepper',
    id: 'w-stepper-room-setpoint',
    label: 'Raum-Sollwert',
    commandPoint: 'demo/room-setpoint',
    feedbackPoint: 'demo/room-setpoint',
    min: 15,
    max: 28,
    step: 0.5,
    unit: '°C',
    decimals: 1,
  },
  {
    kind: 'momentary-button',
    id: 'w-momentary-alarm-reset',
    label: 'Alarm-Reset (Impuls)',
    commandPoint: 'demo/alarm-count',
    pulseValue: 1,
    releaseValue: 0,
    pulseDurationMs: 500,
    confirmBeforeWrite: false,
  },
  {
    kind: 'slider',
    id: 'w-slider-fan-speed',
    label: 'Lüfter-Drehzahl (Slider)',
    commandPoint: 'demo/pump-load',
    feedbackPoint: 'demo/pump-load',
    min: 0,
    max: 1,
    step: 0.05,
    deadband: 0.02,
  },
  {
    kind: 'lock-guard',
    id: 'w-lock-manual',
    label: 'Handbetrieb freigeben',
    commandPoint: 'demo/valve-open',
    feedbackPoint: 'demo/valve-open',
    armValue: true,
    disarmValue: false,
    timeoutSeconds: 60,
    armLabel: 'Freigeben',
    disarmLabel: 'Sperren',
    confirmationCopy: 'Handbetrieb hebt die Regelung auf. Weiter?',
  },
];

const DISPLAY_WIDGETS: WidgetDefinition[] = [
  {
    kind: 'gauge',
    id: 'w-gauge-room',
    label: 'Raum-Temperatur',
    valuePoint: 'demo/room-temperature',
    setpointPoint: 'demo/room-setpoint',
    min: 15,
    max: 28,
    unit: '°C',
    decimals: 1,
    style: 'arc240',
    zones: [
      { from: 15, to: 18, tone: 'alarm' },
      { from: 18, to: 20, tone: 'warn' },
      { from: 20, to: 24, tone: 'ok' },
      { from: 24, to: 26, tone: 'warn' },
      { from: 26, to: 28, tone: 'alarm' },
    ],
  },
  {
    kind: 'gauge',
    id: 'w-gauge-supply',
    label: 'Vorlauf',
    valuePoint: 'demo/supply-temperature',
    min: 20,
    max: 65,
    unit: '°C',
    decimals: 1,
    style: 'radial',
    zones: [
      { from: 45, to: 55, tone: 'ok' },
      { from: 55, to: 60, tone: 'warn' },
      { from: 60, to: 65, tone: 'alarm' },
    ],
  },
  {
    kind: 'gauge',
    id: 'w-gauge-humidity',
    label: 'Rel. Feuchte (Bar)',
    valuePoint: 'demo/humidity',
    min: 30,
    max: 70,
    unit: '%',
    decimals: 0,
    style: 'bar',
    zones: [
      { from: 40, to: 60, tone: 'ok' },
      { from: 30, to: 40, tone: 'warn' },
      { from: 60, to: 70, tone: 'warn' },
    ],
  },
  {
    kind: 'rolling-counter',
    id: 'w-counter-energy',
    label: 'Energie kumuliert',
    valuePoint: 'demo/energy-kwh',
    unit: 'kWh',
    decimals: 0,
    digitWidth: 7,
  },
  {
    kind: 'rolling-counter',
    id: 'w-counter-runtime',
    label: 'Betriebsstunden',
    valuePoint: 'demo/runtime-hours',
    unit: 'h',
    decimals: 1,
    digitWidth: 6,
  },
  {
    kind: 'status-led',
    id: 'w-led-mode',
    label: 'Betriebsmodus',
    statePoint: 'demo/mode',
    showLabel: true,
    mapping: [
      { value: 'heating', colour: '#d0a24c', label: 'Heizen' },
      { value: 'cooling', colour: '#5aa2ff', label: 'Kühlen' },
      { value: 'idle',    colour: '#8a929e', label: 'Bereit' },
      { value: 'defrost', colour: '#d15c5c', label: 'Abtauung' },
    ],
  },
  {
    kind: 'status-led',
    id: 'w-led-compressor',
    label: 'Kompressor',
    statePoint: 'demo/compressor',
    showLabel: true,
    mapping: [
      { value: 'on',  colour: '#62c26e', label: 'Läuft' },
      { value: 'off', colour: '#4a5058', label: 'Aus' },
    ],
  },
  {
    kind: 'alarm-indicator',
    id: 'w-alarm-plant',
    label: 'Anlagen-Alarme',
    scopedToPlant: true,
    showCount: true,
  },
  {
    kind: 'sparkline',
    id: 'w-sparkline-temp',
    label: 'Temperatur-Sparkline',
    valuePoint: 'demo/room-temperature',
    windowSize: 40,
    warnLow: 19,
    warnHigh: 25,
  },
  {
    kind: 'trend',
    id: 'w-trend-multi',
    label: 'Trend Vorlauf + Sollwert',
    defaultRange: 'PT24H',
    liveTail: true,
    series: [
      { point: 'demo/supply-temperature', label: 'Vorlauf',  style: 'line' },
      { point: 'demo/room-setpoint',      label: 'Sollwert', style: 'stepAfter' },
    ],
  },
];

const HVAC_WIDGETS: WidgetDefinition[] = [
  {
    kind: 'schematic-overlay',
    id: 'w-schematic-demo',
    label: 'Anlagenschema (SVG-Overlay)',
    svgId: 'demo-heatpump',
    anims: [
      { elementId: 'valve_v01', target: 'fill', valuePoint: 'demo/valve-open',
        colourRamp: [{ at: 0, colour: '#8a929e' }, { at: 1, colour: '#62c26e' }] },
      { elementId: 'pump_p12', target: 'rotate', valuePoint: 'demo/pump-load', min: 0, max: 1 },
    ],
  },
  {
    kind: 'energy-flow',
    id: 'w-energy-flow-hp',
    label: 'Energiefluss Wärmepumpe',
    copPoint: 'demo/cop',
    powerUnit: 'kW',
    nodes: [
      { id: 'grid',     label: 'Netz' },
      { id: 'ambient',  label: 'Umwelt' },
      { id: 'heat',     label: 'Nutzwärme' },
    ],
    links: [
      { fromNodeId: 'grid',    toNodeId: 'heat', valuePoint: 'demo/pump-load' },
      { fromNodeId: 'ambient', toNodeId: 'heat', valuePoint: 'demo/cop' },
    ],
  },
  {
    kind: 'schedule-editor',
    id: 'w-schedule-week',
    label: 'Wochenprogramm',
    programPoint: 'demo/room-setpoint',
    gridStepMinutes: 30,
    readOnly: true,
  },
  {
    kind: 'stage-cascade',
    id: 'w-cascade-boilers',
    label: 'Kesselkaskade (3 Stufen)',
    loadUnit: '%',
    stages: [
      { label: 'Kessel 1', statePoint: 'demo/compressor',  loadPoint: 'demo/pump-load' },
      { label: 'Kessel 2', statePoint: 'demo/valve-open',  loadPoint: 'demo/pump-load' },
      { label: 'Kessel 3', statePoint: 'demo/mode',        loadPoint: 'demo/pump-load' },
    ],
  },
];

const ASSET_WIDGETS: WidgetDefinition[] = [
  // Wird gefuellt sobald die Tenant-Asset-Slice (backend + Picker) live ist.
  // Aktuell zeigt der Tab einen Hinweis-Text via `cards.length === 0` unten.
];

/** Compound-Tiles — ein Building-Block pro echtem Wohnhaus-Objekt. Reihenfolge
 *  entspricht der typischen Screen-Komposition: Wärmepumpe zuerst (das Herz
 *  der Anlage), dann Heizkreis + Warmwasser + Raum, PV + Wetter als Kontext. */
const COMPOUND_WIDGETS: WidgetDefinition[] = [
  {
    id: 'demo-heat-pump-kpi',
    kind: 'heat-pump-kpi',
    label: 'Wärmepumpe',
    heatPumpName: 'Vaillant aroTHERM 10 kW',
    electricalPowerPoint: 'demo/hp/electrical-power',
    thermalPowerPoint: 'demo/hp/thermal-power',
    statePoint: 'demo/hp/state',
    flowTemperaturePoint: 'demo/hp/flow',
    outsideTemperaturePoint: 'demo/outside/temp',
    runtimeTodayPoint: 'demo/hp/runtime-today',
    runtimeLifetimePoint: 'demo/hp/runtime-lifetime',
    compressorStartsTodayPoint: 'demo/hp/starts-today',
  },
  {
    id: 'demo-heat-circuit',
    kind: 'heat-circuit-tile',
    label: 'Heizkreis EG',
    circuitName: 'Erdgeschoss (Fußboden)',
    flowTemperaturePoint: 'demo/supply-temperature',
    returnTemperaturePoint: 'demo/hp/return',
    setpointPoint: 'demo/room-setpoint',
    setpointMin: 5,
    setpointMax: 30,
    setpointStep: 0.5,
    modePoint: 'demo/mode',
    valvePositionPoint: 'demo/hc/valve',
    pumpStatePoint: 'demo/hc/pump',
    roomTemperaturePoint: 'demo/room-temperature',
    outsideTemperaturePoint: 'demo/outside/temp',
  },
  {
    id: 'demo-hot-water',
    kind: 'hot-water-tile',
    label: 'Warmwasser',
    storageName: 'Pufferspeicher 300 L',
    topTemperaturePoint: 'demo/hw/top',
    midTemperaturePoint: 'demo/hw/mid',
    bottomTemperaturePoint: 'demo/hw/bottom',
    setpointPoint: 'demo/hw/setpoint',
    statePoint: 'demo/hw/state',
    nextLegionellaPoint: 'demo/hw/next-legionella',
  },
  {
    id: 'demo-room-thermo',
    kind: 'room-thermostat',
    label: 'Wohnzimmer',
    roomName: 'Wohnzimmer',
    roomTemperaturePoint: 'demo/room-temperature',
    setpointPoint: 'demo/room-setpoint',
    setpointMin: 10,
    setpointMax: 28,
    setpointStep: 0.5,
    modePoint: 'demo/mode',
    humidityPoint: 'demo/room/humidity',
    windowContactPoint: 'demo/room/window',
    presencePoint: 'demo/room/presence',
  },
  {
    id: 'demo-pv',
    kind: 'pv-self-consumption',
    label: 'Photovoltaik',
    pvPowerPoint: 'demo/pv/production',
    householdPowerPoint: 'demo/pv/household',
    gridPowerPoint: 'demo/pv/grid',
    batterySocPoint: 'demo/pv/battery-soc',
    batteryPowerPoint: 'demo/pv/battery-power',
    dailyProductionEnergyPoint: 'demo/pv/daily-production',
    dailySelfUseEnergyPoint: 'demo/pv/daily-self',
    dailyFeedInEnergyPoint: 'demo/pv/daily-feed',
    dailyGridDrawEnergyPoint: 'demo/pv/daily-grid',
  },
  {
    id: 'demo-weather',
    kind: 'weather-tile',
    label: 'Außenklima',
    outsideTemperaturePoint: 'demo/outside/temp',
    attenuatedTemperaturePoint: 'demo/outside/attenuated',
    humidityPoint: 'demo/humidity',
    windSpeedPoint: 'demo/outside/wind',
    weatherIconPoint: 'demo/outside/icon',
  },
];

const WIDGETS_BY_TAB: Record<TabKey, WidgetDefinition[]> = {
  command:  COMMAND_WIDGETS,
  display:  DISPLAY_WIDGETS,
  hvac:     HVAC_WIDGETS,
  compound: COMPOUND_WIDGETS,
  assets:   ASSET_WIDGETS,
};
